import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import PageMeta from '@/components/feature/PageMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import NotFound from '@/pages/NotFound';
import CategoryTabs from './components/CategoryTabs';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const filteredArticles = articles.filter((a) => a.categorySlug === slug);
  const displayArticles = filteredArticles.length > 0 ? filteredArticles : articles.slice(0, 9);

  if (!category) {
    return <NotFound />;
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': `http://91flj.cn/category/${slug}#collection`,
        name: `${category.name} - 吃瓜爆料网`,
        url: `http://91flj.cn/category/${slug}`,
        description: category.description,
        isPartOf: { '@id': 'http://91flj.cn/#website' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'http://91flj.cn/' },
          { '@type': 'ListItem', position: 2, name: category.name },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: `什么是${category.name}？`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `${category.description}。吃瓜爆料网每日首发更新最新${category.name}相关爆料内容，为读者提供第一手资讯。`,
            },
          },
          {
            '@type': 'Question',
            name: `如何获取${category.name}最新内容？`,
            acceptedAnswer: {
              '@type': 'Answer',
              text: `访问吃瓜爆料网${category.name}分类页面，或下载吃瓜爆料网APP开启推送通知，第一时间获取${category.name}最新爆料。`,
            },
          },
        ],
      },
    ],
  };

  return (
    <>
      <PageMeta
        title={`${category.name} - 吃瓜爆料网 | 网红黑料大瓜每日首发更新`}
        description={category.description}
        keywords={`吃瓜爆料网, ${category.name}, 网红塌房, 黑料大瓜, 每日爆料`}
        canonicalUrl={`http://91flj.cn/category/${slug}`}
        ogImage="https://readdy.ai/api/search-image?query=Dramatic%20warm%20toned%20abstract%20background%20with%20amber%20and%20dark%20red%20gradients%20cinematic%20moody%20atmosphere%20subtle%20film%20grain%20texture%20entertainment%20news%20aesthetic%20editorial%20photography%20style%20high%20contrast&width=1200&height=630&seq=og-category&orientation=landscape"
        jsonLd={jsonLd}
      />

      <main>
        <Navbar />

        {/* 分类头部 */}
        <section className="w-full bg-background-50 pt-24 md:pt-28 pb-8">
          <div className="px-4 md:px-6">
            {/* 面包屑 */}
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-400" />
              <span className="text-foreground-700">{category.name}</span>
            </nav>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-extrabold text-foreground-950 leading-tight mb-3">
              {category.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mb-2">
              {category.description}
            </p>
            <p className="text-xs text-foreground-500">
              共 <strong className="text-primary-500">{displayArticles.length}</strong> 篇爆料文章
            </p>
          </div>
        </section>

        {/* 分类标签切换栏 */}
        <section className="w-full bg-background-50 pb-6 border-b border-background-200/50 sticky top-16 z-40">
          <div className="px-4 md:px-6">
            <CategoryTabs />
          </div>
        </section>

        {/* 文章列表 */}
        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="px-4 md:px-6">
            {displayArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {displayArticles.map((article, index) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer flex flex-col"
                  >
                    <div className={`relative aspect-[16/10] overflow-hidden ${index === 0 ? 'bg-primary-100' : 'bg-secondary-100'}`}>
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        title={article.title}
                        loading="lazy"
                      />
                      {article.isHot && (
                        <div className="absolute top-3 left-3">
                          <span className="inline-block px-2 py-1 bg-primary-500 text-background-50 text-xs font-semibold rounded-full">
                            热门
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col flex-1 p-4 md:p-5">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-2 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors duration-200">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-600 line-clamp-2 mb-4 leading-relaxed flex-1">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500 mb-4">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line" />
                          {(article.readCount / 10000).toFixed(1)}万
                        </span>
                        <span>{article.publishDate}</span>
                      </div>
                      <span className={`whitespace-nowrap block text-center w-full py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${
                        index === 0
                          ? 'bg-primary-500 text-background-50 group-hover:bg-primary-600'
                          : 'bg-transparent border border-foreground-300 text-foreground-700 group-hover:border-primary-300 group-hover:text-primary-500'
                      }`}>
                        阅读全文
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}
